import { useNavigate, Outlet } from 'react-router-dom';
import TopNavigation from '@amzn/awsui-components-console/top-navigation';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { APP_DISPLAY_NAME } from '../constants/appConstants';

/**
 * GlobalLayout Component
 *
 * Layout route component that renders a persistent TopNavigation header
 * and an <Outlet /> for nested route content. Used as a pathless layout
 * route in React Router v6.
 *
 * Requirements:
 * - 1.1: Render TopNavigation with high-contrast theme on every page
 * - 1.2: Display logo and title "AWS Mission Networking Portal"
 * - 1.3: Include utility items for Documentation and User profile
 * - 1.4: Documentation utility navigates to /documentation
 * - 1.5: Logo/title click navigates to landing page
 */
export function GlobalLayout() {
  const navigate = useNavigate();
  const { authState, logout } = useAuth();
  const { mode, toggleMode } = useTheme();

  const handleSignOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <>
      <TopNavigation
        identity={{
          href: '/',
          title: APP_DISPLAY_NAME,
          onFollow: (event) => {
            event.preventDefault();
            navigate('/');
          },
        }}
        utilities={[
          {
            type: 'button',
            iconName: mode === 'dark' ? 'status-positive' : 'status-stopped',
            text: mode === 'dark' ? 'Light mode' : 'Dark mode',
            ariaLabel: mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
            onClick: toggleMode,
          },
          {
            type: 'button',
            text: 'Tools',
            onClick: (event) => {
              event.preventDefault();
              navigate('/documentation/getting-started/vpn-key-tools');
            },
          },
          {
            type: 'button',
            text: 'Documentation',
            onClick: (event) => {
              event.preventDefault();
              navigate('/documentation');
            },
          },
          {
            type: 'menu-dropdown',
            text: authState.email ?? 'User',
            iconName: 'user-profile',
            items: [{ id: 'sign-out', text: 'Sign out' }],
            onItemClick: ({ detail }) => {
              if (detail.id === 'sign-out') {
                handleSignOut();
              }
            },
          },
        ]}
        i18nStrings={{
          overflowMenuTriggerText: 'More',
          overflowMenuTitleText: 'All',
        }}
      />
      <Outlet />
    </>
  );
}
